// @flow

import React from 'react';
import _ from 'lodash';

import Button from 'client/components/button/button';
import InputGroup from 'client/components/form/inputGroup';
import { PasswordRequirements, isPasswordValid } from 'client/components/form/passwordRequirements';
import Translate from 'client/components/translate';
import { isValidEmail } from 'client/utils/validate';

import type { TranslateType } from 'types/translateType.flow';

type ErrorFields = {
  email?: TranslateType,
  password?: TranslateType,
};

type Props = {
  buttonClassName: string,
  changeHandler: Function,
  className: string | null,
  ctaClassName: string,
  ctaPath: string,
  emailPlaceholder: string,
  formType: 'login' | 'create',
  isSubmitting: boolean,
  noLabelAnimate: boolean,
  passwordPlaceholder: string,
  submitHandler: Function,
  suppressError: boolean,
};

type State = {
  email: string,
  error: ErrorFields,
  password: string,
};

const TRANSLATE_PREFIX = 'common.account';

const isEmpty = val => (_.isEmpty(val) ? { path: 'common.form.error.required' } : undefined);
const validateEmail = val => (isValidEmail(val) ? undefined : { path: 'common.error.invalidEmail' });
const validatePasswordCreate = val =>
  (isPasswordValid(val) ? undefined : { path: 'common.error.passwordRequirements' });

const validatePasswordLogin = val =>
  (val.length >= 8 ? undefined : { path: 'common.error.passwordLength' });

class AccountForm extends React.PureComponent<Props, State> {
  static defaultProps = {
    buttonClassName: 'cta-button margin-top',
    changeHandler: () => {},
    className: null,
    ctaClassName: '',
    ctaPath: '',
    emailPlaceholder: '',
    isSubmitting: false,
    passwordPlaceholder: '',
    suppressError: false,
  };

  state = {
    email: '',
    password: '',
    error: {},
  };

  onSubmit = (e: Event) => {
    e.preventDefault();
    const { email, password, error } = this.state;
    const { submitHandler, suppressError, formType } = this.props;
    const nextErrorState = {
      email: error.email || isEmpty(email) || validateEmail(email),
      password:
        error.password ||
        isEmpty(password) ||
        (formType === 'create' ? validatePasswordCreate(password) : validatePasswordLogin(password)),
    };
    if ((!_.isEmpty(nextErrorState.email) || !_.isEmpty(nextErrorState.password)) && !suppressError) {
      return this.setState({ error: nextErrorState });
    }
    return submitHandler({ email, password });
  };

  handleChange = (e: SyntheticInputEvent<*>) => {
    const { id, value } = e.currentTarget;

    this.setState(
      {
        [id]: value,
        error: { ...this.state.error, [id]: null },
      },
      () => {
        const { email, password } = this.state;
        this.props.changeHandler({ email, password });
      },
    );
  };

  validateInput = (e: SyntheticInputEvent<*>) => {
    const { id, value } = e.currentTarget;
    const { formType } = this.props;
    let error = isEmpty(value);
    if (!error && id === 'email') {
      error = validateEmail(value);
    } else if (!error && id === 'password' && formType === 'create') {
      error = validatePasswordCreate(value);
    } else if (!error && id === 'password' && formType === 'login') {
      error = validatePasswordLogin(value);
    }
    this.setState({
      error: { ...this.state.error, [id]: error },
    });
  };

  render() {
    const {
      buttonClassName,
      className,
      ctaClassName,
      ctaPath,
      emailPlaceholder,
      formType,
      noLabelAnimate,
      passwordPlaceholder,
      isSubmitting,
      suppressError,
    } = this.props;
    const { email, error, password } = this.state;
    const fallbackCta = `${TRANSLATE_PREFIX}.${formType}.cta`;

    const isCreateForm = formType === 'create';
    const isFormValid = isCreateForm
      ? !_.isEmpty(email) && isValidEmail(email) && isPasswordValid(password)
      : !_.isEmpty(email) && !_.isEmpty(password);

    return (
      <div className={className}>
        <form onSubmit={this.onSubmit}>
          <InputGroup
            error={suppressError ? false : error.email}
            id="email"
            label={{
              path: 'common.label.email',
            }}
            noLabelAnimate={noLabelAnimate}
            onBlur={this.validateInput}
            onChange={this.handleChange}
            placeholder={emailPlaceholder}
            suppressError={suppressError}
            type="text"
            value={email} />
          <InputGroup
            autoComplete="off"
            error={suppressError ? false : error.password}
            id="password"
            label={{
              path: 'common.label.password',
            }}
            noLabelAnimate={noLabelAnimate}
            onBlur={this.validateInput}
            onChange={this.handleChange}
            placeholder={passwordPlaceholder}
            suppressError={suppressError}
            type="password"
            value={password} />
          {isCreateForm && <PasswordRequirements password={password} className="margin-top-small" />}
          <Button className={buttonClassName} isDisabled={isSubmitting || !isFormValid} type="submit">
            <Translate className={ctaClassName} path={ctaPath || fallbackCta} />
          </Button>
        </form>
      </div>
    );
  }
}

export default AccountForm;
